import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation, Redirect } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Layout from "./components/Layout";
import FloatingContact from "./components/FloatingContact";

// Eagerly load Home page (critical for LCP)
import Home from "./pages/Home";

// Lazy load all other pages to reduce initial bundle size and TBT
const About = lazy(() => import("./pages/About"));
const YourPathForward = lazy(() => import("./pages/YourPathForward"));
const Communities = lazy(() => import("./pages/Communities"));
const WholesalingTruth = lazy(() => import("./pages/WholesalingTruth"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const WhyWereDifferent = lazy(() => import("./pages/WhyWereDifferent"));
const CreativeFinancing = lazy(() => import("./pages/CreativeFinancing"));
const Projects = lazy(() => import("./pages/Projects"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const ContactThankYou = lazy(() => import("./pages/ContactThankYou"));
const LakeBonavistaLanding = lazy(() => import("./pages/LakeBonavistaLanding"));
const LandingThankYou = lazy(() => import("./pages/LandingThankYou"));
const ListingThankYou = lazy(() => import("./pages/ListingThankYou"));
const CreativeFinancingThankYou = lazy(() => import("./pages/CreativeFinancingThankYou"));
const NeighborhoodLanding = lazy(() => import("./pages/NeighborhoodLanding"));
const ListYourHome = lazy(() => import("./pages/ListYourHome"));

// Declare Umami types for TypeScript
declare global {
  interface Window {
    umami?: {
      track: (event?: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Minimal loading fallback to avoid layout shift
function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[oklch(0.45_0.12_145)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const [pathname] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Manual page view tracking for Umami (auto-track disabled to avoid deprecated unload API)
    if (window.umami) {
      window.umami.track();
    }
  }, [pathname]);

  return null;
}

function Router() {
  return (
    <Switch>
      {/* Landing pages - no Layout wrapper (no navigation) */}
      <Route path="/lake-bonavista">
        <Suspense fallback={<PageLoader />}>
          <LakeBonavistaLanding />
        </Suspense>
      </Route>
      <Route path="/landing-thank-you">
        <Suspense fallback={<PageLoader />}>
          <LandingThankYou />
        </Suspense>
      </Route>
      <Route path="/listing-thank-you">
        <Suspense fallback={<PageLoader />}>
          <ListingThankYou />
        </Suspense>
      </Route>
      <Route path="/creative-financing-thank-you">
        <Suspense fallback={<PageLoader />}>
          <CreativeFinancingThankYou />
        </Suspense>
      </Route>
      
      {/* Main site with Layout */}
      <Route>
        <Layout>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Switch>
              <Route path="/" component={Home} />
              <Route path="/about" component={About} />
              <Route path="/your-path-forward" component={YourPathForward} />
              {/* 301 Redirects for old URLs */}
              <Route path="/process">
                <Redirect to="/your-path-forward" />
              </Route>
              <Route path="/how-it-works">
                <Redirect to="/your-path-forward" />
              </Route>
              <Route path="/communities" component={Communities} />
              <Route path="/wholesaling-truth" component={WholesalingTruth} />
              <Route path="/faq" component={FAQPage} />
              <Route path="/why-were-different" component={WhyWereDifferent} />
              <Route path="/why-sell-to-us">
                <Redirect to="/why-were-different" />
              </Route>
              <Route path="/creative-financing" component={CreativeFinancing} />
              <Route path="/projects" component={Projects} />
              <Route path="/list-your-home" component={ListYourHome} />
              <Route path="/thank-you" component={ThankYou} />
              <Route path="/contact-thank-you" component={ContactThankYou} />
              {/* Dynamic neighborhood landing pages - must be last before NotFound */}
              <Route path="/:neighborhood">
                {(params) => <NeighborhoodLanding />}
              </Route>
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </Layout>
      </Route>
    </Switch>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <TooltipProvider>
          <Toaster />
          <Router />
          <FloatingContact />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
